import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import PageWrapper from "./layout/PageWrapper";
import PageHeader from "./layout/PageHeader";
import StatsCard from "./layout/StatsCard";
import FormStatsDateFilter from "./FormStatsDateFilter";
import DescriptionIcon from "@mui/icons-material/Description";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from "@mui/icons-material/Today";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";

const AdminFormStats = ({
  stats,
  periodType,
  startDate,
  endDate,
  onPeriodTypeChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  onRefreshData,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getTrendText = (trend) => {
    if (trend > 0) return `+${trend}%`;
    if (trend < 0) return `${trend}%`;
    return "0%";
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return "success";
    if (trend < 0) return "error";
    return "default";
  };

  return (
    <PageWrapper>
      <PageHeader
        title="Statistiques des Formulaires"
        subtitle="Analysez l'évolution des formulaires créés"
      />

      {/* Filtres de période */}
      <FormStatsDateFilter
        periodType={periodType}
        startDate={startDate}
        endDate={endDate}
        onPeriodTypeChange={onPeriodTypeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onApplyFilters={onApplyFilters}
        onRefresh={onRefreshData}
      />

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Formulaires"
            value={stats.total}
            icon={DescriptionIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tendance"
            value={getTrendText(stats.trend)}
            icon={TrendingUpIcon}
            color={getTrendColor(stats.trend)}
            trend={
              stats.trend > 0 ? "up" : stats.trend < 0 ? "down" : "neutral"
            }
            trendValue={getTrendText(stats.trend)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Moyenne / Jour"
            value={stats.averagePerDay}
            icon={TodayIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Utilisateurs"
            value={stats.byUser.length}
            icon={PeopleIcon}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Graphiques et tableaux */}
      <Grid container spacing={3}>
        {/* Graphique par date */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <BarChartIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6" component="h2">
                  Formulaires par Date
                </Typography>
              </Box>

              {stats.byDate.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Nombre</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Graphique</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byDate.map((item, index) => {
                        const maxCount = Math.max(
                          ...stats.byDate.map((d) => d.count)
                        );
                        const barWidth = (item.count / maxCount) * 100;

                        return (
                          <TableRow key={index} hover>
                            <TableCell>{formatDate(item.date)}</TableCell>
                            <TableCell align="right">{item.count}</TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${barWidth}%`,
                                    minWidth: "20px",
                                    height: "20px",
                                    backgroundColor: "primary.main",
                                    borderRadius: 1,
                                  }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  Aucune donnée disponible pour cette période
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top utilisateurs */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PeopleIcon sx={{ mr: 1, color: "secondary.main" }} />
                <Typography variant="h6" component="h2">
                  Top Utilisateurs
                </Typography>
              </Box>

              {stats.byUser.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Utilisateur</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Formulaires</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byUser.slice(0, 10).map((user, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {user.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "primary.main" }}
                            >
                              {user.count}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  Aucun utilisateur
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default AdminFormStats;
